import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Loader2, Pencil, Plus, Trash2, Check, CheckCircle2, Circle,
  Video, Paperclip, Download, Upload, X, Save, ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getCourse, setLessonProgress, canEditLibrary,
  upsertCourse, upsertModule, deleteModule, upsertLesson, deleteLesson,
  signTrainingAttachment,
} from "@/lib/training.functions";
import { parseVideoUrl } from "@/lib/video-embed";
import { supabase } from "@/integrations/supabase/client";
import { CoverImageUploader } from "@/components/cover-image-uploader";

export const Route = createFileRoute("/_authenticated/training/courses/$courseId")({
  component: CoursePage,
});

type Attachment = { name: string; path: string; size?: number; mime?: string };

function CoursePage() {
  const { courseId } = useParams({ from: "/_authenticated/training/courses/$courseId" });
  const qc = useQueryClient();
  const fetchCourse = useServerFn(getCourse);
  const setProgress = useServerFn(setLessonProgress);
  const canEditFn = useServerFn(canEditLibrary);
  const saveCourseFn = useServerFn(upsertCourse);
  const saveModuleFn = useServerFn(upsertModule);
  const deleteModuleFn = useServerFn(deleteModule);
  const saveLessonFn = useServerFn(upsertLesson);
  const deleteLessonFn = useServerFn(deleteLesson);
  const signFn = useServerFn(signTrainingAttachment);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["training-course", courseId],
    queryFn: () => fetchCourse({ data: { course_id: courseId } }),
  });

  const canEditQuery = useQuery({
    queryKey: ["training-can-edit", data?.library?.id],
    queryFn: () => canEditFn({ data: { library_id: data!.library!.id } }),
    enabled: !!data?.library?.id,
  });
  const canEdit = !!canEditQuery.data?.canEdit;

  const [editMode, setEditMode] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);

  const lessons = data?.lessons ?? [];
  const modules = data?.modules ?? [];
  const progress = data?.progress ?? {};

  // Auto-select the first lesson once data loads
  useEffect(() => {
    if (!selectedLessonId && lessons.length > 0) {
      setSelectedLessonId(lessons[0].id);
    }
  }, [lessons, selectedLessonId]);

  const selectedLesson = useMemo(
    () => lessons.find((l: any) => l.id === selectedLessonId) ?? null,
    [lessons, selectedLessonId]
  );

  const markMut = useMutation({
    mutationFn: async ({ lesson_id, completed }: { lesson_id: string; completed: boolean }) =>
      setProgress({ data: { lesson_id, completed } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["training-course", courseId] }),
  });

  // ---------- Edit handlers ----------

  const [courseEdit, setCourseEdit] = useState<{ title: string; description: string; cover_image_url: string } | null>(null);
  useEffect(() => {
    if (data?.course && editMode) {
      setCourseEdit({
        title: data.course.title,
        description: data.course.description ?? "",
        cover_image_url: data.course.cover_image_url ?? "",
      });
    }
  }, [editMode, data?.course]);

  const saveCourseMut = useMutation({
    mutationFn: async () => {
      if (!data?.course || !courseEdit) return;
      return saveCourseFn({ data: {
        id: data.course.id,
        library_id: data.course.library_id,
        title: courseEdit.title.trim(),
        description: courseEdit.description.trim() || null,
        cover_image_url: courseEdit.cover_image_url.trim() || null,
      }});
    },
    onSuccess: () => { toast.success("Course saved"); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const [moduleDialog, setModuleDialog] = useState<{ id?: string; title: string; description: string } | null>(null);
  const saveModuleMut = useMutation({
    mutationFn: async () => {
      if (!moduleDialog) return;
      return saveModuleFn({ data: {
        id: moduleDialog.id,
        course_id: courseId,
        title: moduleDialog.title.trim(),
        description: moduleDialog.description.trim() || null,
        display_order: moduleDialog.id ? undefined : modules.length,
      }});
    },
    onSuccess: () => { toast.success("Module saved"); setModuleDialog(null); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const deleteModuleMut = useMutation({
    mutationFn: async (id: string) => deleteModuleFn({ data: { id } }),
    onSuccess: () => { toast.success("Module deleted"); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  type LessonDraft = {
    id?: string;
    module_id: string;
    title: string;
    video_url: string;
    content_html: string;
    attachments: Attachment[];
    is_published: boolean;
  };
  const [lessonDialog, setLessonDialog] = useState<LessonDraft | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const saveLessonMut = useMutation({
    mutationFn: async () => {
      if (!lessonDialog) return;
      const moduleLessons = lessons.filter((l: any) => l.module_id === lessonDialog.module_id);
      return saveLessonFn({ data: {
        id: lessonDialog.id,
        module_id: lessonDialog.module_id,
        title: lessonDialog.title.trim(),
        video_url: lessonDialog.video_url.trim() || null,
        content_html: lessonDialog.content_html.trim() || null,
        attachments: lessonDialog.attachments,
        is_published: lessonDialog.is_published,
        display_order: lessonDialog.id ? undefined : moduleLessons.length,
      }});
    },
    onSuccess: () => { toast.success("Lesson saved"); setLessonDialog(null); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const deleteLessonMut = useMutation({
    mutationFn: async (id: string) => deleteLessonFn({ data: { id } }),
    onSuccess: () => { toast.success("Lesson deleted"); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Delete failed"),
  });

  async function handleUploadAttachment(file: File) {
    if (!lessonDialog) return;
    const safe = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
    const path = `${courseId}/${crypto.randomUUID()}-${safe}`;
    const { error } = await supabase.storage.from("training-attachments").upload(path, file);
    if (error) { toast.error(error.message); return; }
    setLessonDialog({
      ...lessonDialog,
      attachments: [...lessonDialog.attachments, { name: file.name, path, size: file.size, mime: file.type }],
    });
    toast.success("Attached");
  }

  async function openAttachment(att: Attachment) {
    try {
      const { url } = await signFn({ data: { path: att.path } });
      if (url) window.open(url, "_blank");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not open file");
    }
  }

  if (isLoading || !data) {
    return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const { course } = data;
  const parsedVideo = parseVideoUrl(selectedLesson?.video_url);

  return (
    <div className="flex flex-col h-full">
      <div className="border-b px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Button asChild variant="ghost" size="sm">
            <Link to="/training"><ArrowLeft className="h-4 w-4 mr-1" /> Library</Link>
          </Button>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <h1 className="text-lg font-semibold truncate">{course.title}</h1>
        </div>
        {canEdit && (
          <Button size="sm" variant={editMode ? "default" : "outline"} onClick={() => setEditMode((v) => !v)}>
            <Pencil className="h-4 w-4 mr-1" /> {editMode ? "Done editing" : "Edit course"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-[320px,1fr] flex-1 min-h-0">
        {/* Sidebar: modules + lessons */}
        <aside className="border-r overflow-y-auto p-3 space-y-4 bg-muted/20">
          {modules.length === 0 && !editMode && (
            <p className="text-sm text-muted-foreground p-3">No modules yet.</p>
          )}
          {modules.map((m: any) => {
            const mlessons = lessons.filter((l: any) => l.module_id === m.id);
            return (
              <div key={m.id} className="space-y-1">
                <div className="flex items-center justify-between gap-1 px-1">
                  <div className="font-medium text-sm">{m.title}</div>
                  {editMode && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6"
                        onClick={() => setModuleDialog({ id: m.id, title: m.title, description: m.description ?? "" })}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive"
                        onClick={() => {
                          if (confirm(`Delete module "${m.title}" and all its lessons?`)) deleteModuleMut.mutate(m.id);
                        }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <ul className="space-y-0.5">
                  {mlessons.map((l: any) => {
                    const isSel = l.id === selectedLessonId;
                    const done = !!progress[l.id]?.completed_at;
                    return (
                      <li key={l.id}>
                        <button
                          onClick={() => setSelectedLessonId(l.id)}
                          className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${
                            isSel ? "bg-primary/10 text-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="h-4 w-4 shrink-0" />}
                          <span className="truncate flex-1">{l.title}</span>
                          {!l.is_published && <Badge variant="outline" className="text-[10px] h-4 px-1">Draft</Badge>}
                        </button>
                      </li>
                    );
                  })}
                  {editMode && (
                    <li>
                      <Button size="sm" variant="ghost" className="w-full justify-start text-muted-foreground"
                        onClick={() => setLessonDialog({
                          module_id: m.id, title: "", video_url: "", content_html: "",
                          attachments: [], is_published: true,
                        })}>
                        <Plus className="h-3 w-3 mr-1" /> Add lesson
                      </Button>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
          {editMode && (
            <Button size="sm" variant="outline" className="w-full"
              onClick={() => setModuleDialog({ title: "", description: "" })}>
              <Plus className="h-3 w-3 mr-1" /> Add module
            </Button>
          )}
        </aside>

        {/* Main: lesson player */}
        <main className="overflow-y-auto p-6">
          {editMode && courseEdit && (
            <Card className="mb-6">
              <CardContent className="p-4 space-y-3">
                <div className="text-sm font-medium">Course details</div>
                <div>
                  <Label>Title</Label>
                  <Input value={courseEdit.title} onChange={(e) => setCourseEdit({ ...courseEdit, title: e.target.value })} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={courseEdit.description} onChange={(e) => setCourseEdit({ ...courseEdit, description: e.target.value })} rows={2} />
                </div>
                <div>
                  <Label>Cover image</Label>
                  <CoverImageUploader
                    value={courseEdit.cover_image_url}
                    onChange={(url) => setCourseEdit({ ...courseEdit, cover_image_url: url })}
                  />
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => saveCourseMut.mutate()} disabled={saveCourseMut.isPending}>
                    {saveCourseMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save course
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!selectedLesson ? (
            <Card><CardContent className="p-10 text-center text-muted-foreground">
              Select a lesson to begin.
            </CardContent></Card>
          ) : (
            <article className="space-y-4 max-w-4xl">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-2xl font-semibold">{selectedLesson.title}</h2>
                <div className="flex items-center gap-2">
                  {editMode && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setLessonDialog({
                        id: selectedLesson.id,
                        module_id: selectedLesson.module_id,
                        title: selectedLesson.title,
                        video_url: selectedLesson.video_url ?? "",
                        content_html: selectedLesson.content_html ?? "",
                        attachments: (selectedLesson.attachments as Attachment[]) ?? [],
                        is_published: selectedLesson.is_published,
                      })}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => {
                        if (confirm(`Delete lesson "${selectedLesson.title}"?`)) deleteLessonMut.mutate(selectedLesson.id);
                      }}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant={progress[selectedLesson.id]?.completed_at ? "default" : "outline"}
                    onClick={() => markMut.mutate({
                      lesson_id: selectedLesson.id,
                      completed: !progress[selectedLesson.id]?.completed_at,
                    })}
                  >
                    {progress[selectedLesson.id]?.completed_at
                      ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Completed</>
                      : <><Check className="h-4 w-4 mr-1" /> Mark complete</>}
                  </Button>
                </div>
              </div>

              {parsedVideo ? (
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                  <iframe
                    src={parsedVideo.embedUrl}
                    className="w-full h-full"
                    title={selectedLesson.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : selectedLesson.video_url ? (
                <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Video URL not recognized (use YouTube, Loom, Vimeo, or Wistia).
                </CardContent></Card>
              ) : null}

              {selectedLesson.content_html && (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedLesson.content_html }}
                />
              )}

              {selectedLesson.attachments && (selectedLesson.attachments as Attachment[]).length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Attachments</div>
                  <ul className="space-y-1">
                    {(selectedLesson.attachments as Attachment[]).map((att, i) => (
                      <li key={i}>
                        <button onClick={() => openAttachment(att)}
                          className="text-sm text-primary hover:underline flex items-center gap-1.5">
                          <Download className="h-3 w-3" /> {att.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          )}
        </main>
      </div>

      {/* Module dialog */}
      <Dialog open={!!moduleDialog} onOpenChange={(o) => !o && setModuleDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{moduleDialog?.id ? "Edit module" : "New module"}</DialogTitle></DialogHeader>
          {moduleDialog && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={moduleDialog.title} onChange={(e) => setModuleDialog({ ...moduleDialog, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={moduleDialog.description} onChange={(e) => setModuleDialog({ ...moduleDialog, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(null)}>Cancel</Button>
            <Button onClick={() => saveModuleMut.mutate()} disabled={!moduleDialog?.title.trim() || saveModuleMut.isPending}>
              {saveModuleMut.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson dialog */}
      <Dialog open={!!lessonDialog} onOpenChange={(o) => !o && setLessonDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{lessonDialog?.id ? "Edit lesson" : "New lesson"}</DialogTitle></DialogHeader>
          {lessonDialog && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label>Title</Label>
                <Input value={lessonDialog.title} onChange={(e) => setLessonDialog({ ...lessonDialog, title: e.target.value })} />
              </div>
              <div>
                <Label>Video URL <span className="text-xs text-muted-foreground">(YouTube, Loom, Vimeo, Wistia)</span></Label>
                <Input value={lessonDialog.video_url}
                  onChange={(e) => setLessonDialog({ ...lessonDialog, video_url: e.target.value })}
                  placeholder="https://…" />
                {lessonDialog.video_url && (
                  <div className="text-xs mt-1">
                    {parseVideoUrl(lessonDialog.video_url)
                      ? <span className="text-emerald-600">✓ Detected {parseVideoUrl(lessonDialog.video_url)!.provider}</span>
                      : <span className="text-amber-600">URL not recognized</span>}
                  </div>
                )}
              </div>
              <div>
                <Label>Content (HTML allowed)</Label>
                <Textarea rows={5} value={lessonDialog.content_html}
                  onChange={(e) => setLessonDialog({ ...lessonDialog, content_html: e.target.value })}
                  placeholder="Lesson notes, transcript, or instructions…" />
              </div>
              <div>
                <Label>Attachments</Label>
                <div className="space-y-1.5">
                  {lessonDialog.attachments.map((att, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border rounded-md px-2 py-1">
                      <span className="truncate flex items-center gap-1.5"><Paperclip className="h-3 w-3" /> {att.name}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6"
                        onClick={() => setLessonDialog({
                          ...lessonDialog,
                          attachments: lessonDialog.attachments.filter((_, j) => j !== i),
                        })}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <input ref={fileRef} type="file" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUploadAttachment(f);
                      e.target.value = "";
                    }} />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                    <Upload className="h-3 w-3 mr-1" /> Upload file
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={lessonDialog.is_published}
                    onCheckedChange={(v) => setLessonDialog({ ...lessonDialog, is_published: v })} />
                  Published
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog(null)}>Cancel</Button>
            <Button onClick={() => saveLessonMut.mutate()} disabled={!lessonDialog?.title.trim() || saveLessonMut.isPending}>
              {saveLessonMut.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
